import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { FaBirthdayCake, FaUsers, FaGamepad, FaCalendarAlt, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';
import futuristicpower from '../assets/futuristicfirepower.webp';
import changinbattlegrounds from '../assets/changingbattle.webp';
import { Helmet } from 'react-helmet-async';
import { obtenerSettings } from '../api';
import ParticleBackground from "../components/ParticleBackground";

// Brand palette aligned with GrowthSections
const brand = {
  nearBlack: '#141318',
  darkGray: '#1F2937',
  light: '#E9EAE8',
  white: '#FFFFFF',
  blueGray: '#566D87',
  gold: '#FFD404',
  ink: '#00181C',
};
const Services = () => {
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [privateRef, privateInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [publicRef, publicInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [partyRef, partyInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [contactRef, contactInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await obtenerSettings();
        // Parse opening_hours if needed
        if (typeof data?.opening_hours === 'string') {
          try {
            data.opening_hours = JSON.parse(data.opening_hours);
          } catch (err) {
            data.opening_hours = null;
          }
        }
        setSettings(data);
      } catch (err) {
        setSettings(null);
      }
    };
    fetchSettings();
  }, []);

  return (
    <>
      <Helmet>
        {/* Título y descripción específicos */}
        <title>Our Laser Tag Services – Lazer Zone Arena | Cornwall, ON</title>
        <meta
          name="description"
          content="Explore private laser tag sessions, public matches and party packages at Lazer Zone Arena. Premier laser tag experiences in Cornwall, ON."
        />
        <link rel="canonical" href="https://lazerzonearena.ca/services" />

        {/* Open Graph */}
        <meta property="og:type"        content="website" />
        <meta property="og:url"         content="https://lazerzonearena.ca/services" />
        <meta property="og:title"       content="Our Laser Tag Services – Lazer Zone Arena" />
        <meta
          property="og:description"
          content="Explore private laser tag sessions, public matches and party packages at Lazer Zone Arena. Premier laser tag experiences in Cornwall, ON."
        />
        <meta property="og:image"       content="https://lazerzonearena.ca/logo512.png" />

        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content="Our Laser Tag Services – Lazer Zone Arena" />
        <meta
          name="twitter:description"
          content="Explore private laser tag sessions, public matches and party packages at Lazer Zone Arena. Premier laser tag experiences in Cornwall, ON."
        />
        <meta name="twitter:image"       content="https://lazerzonearena.ca/logo192.png" />

        {/* JSON-LD: catálogo de ofertas y servicios */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            "name": "Laser Tag Services",
            "url": "https://lazerzonearena.ca/services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Private Laser Tag Sessions",
                  "description": "Book our arena exclusively for your group. Enjoy uninterrupted play in your chosen game mode."
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Public Laser Tag Matches",
                  "description": "Walk in and join a live match open to all. Perfect for spontaneous players."
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Birthday & Party Packages",
                  "description": "Combine laser tag gameplay with party lounge rental for the ultimate celebration."
                }
              }
            ]
          }
          `}
        </script>
      </Helmet>
    <div className="font-inter" style={{ backgroundColor: brand.nearBlack, color: brand.light }}>
      <style>{`
        h1, h2, h3 {
          font-family: 'Audiowide', sans-serif;
          letter-spacing: 1px;
        }
        .bg-near-black { background-color: ${brand.nearBlack}; }
        .bg-dark-gray { background-color: ${brand.darkGray}; }
        .text-blue-gray { color: ${brand.blueGray}; }
        .bg-blue-gray { background-color: ${brand.blueGray}; }
        .border-blue-gray { border-color: ${brand.blueGray}; }
        .glow-text { text-shadow: 0 0 10px rgba(86, 109, 135, 0.7); }
        .hover-glow:hover { 
          box-shadow: 0 0 15px rgba(86, 109, 135, 0.5);
          transform: translateY(-5px);
        }
        .transition-smooth { transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1); }
        .text-light-title { color: ${brand.white}; }
      `}</style>

      {/* Header Section */}
      <section
        ref={headerRef}
        className={`relative overflow-hidden py-28 px-6 text-center transition-smooth ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{
          background: `radial-gradient(900px 400px at 10% -10%, rgba(255, 212, 4, 0.08), transparent 70%), radial-gradient(900px 500px at 110% 10%, rgba(86, 109, 135, 0.08), transparent 70%), ${brand.darkGray}`
        }}
      >
        <ParticleBackground className="opacity-30" />
        <div className="relative">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-light-title glow-text"> {/* Cambiado a text-light-title */}
            What We Offer
          </h1>
          <p className="text-light-title text-xl max-w-3xl mx-auto leading-relaxed">
            Explore our <span className="text-blue-gray font-medium">laser tag experiences</span> designed for every kind of player — whether you're celebrating, competing, or just jumping into the action.
          </p>
          <div className="mt-12 flex justify-center gap-4">
            <div className="w-16 h-1 rounded-full" style={{ backgroundColor: brand.gold }}></div>
            <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brand.gold, opacity: 0.8 }}></div>
            <div className="w-4 h-1 rounded-full" style={{ backgroundColor: brand.gold, opacity: 0.6 }}></div>
          </div>
        </div>
      </section>

      {/* Private Game Mode */}
      <section className="py-20 px-6" style={{ backgroundColor: brand.nearBlack }}>
        <div 
          ref={privateRef}
          className={`max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center transition-smooth ${privateInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <ServiceCard 
            title="Private Laser Tag Sessions"
            text="Book our arena exclusively for your group. Enjoy uninterrupted play in your chosen game mode — perfect for private events, family fun, or squad competitions."
            image={futuristicpower}
            icon={<FaUsers className="text-3xl text-blue-gray" />}
            titleColor="text-light-title" 
          />
          <div className="text-left">
            <div className="flex items-center mb-6">
              <div className="mr-4 p-3 rounded-full" style={{ backgroundColor: 'rgba(86, 109, 135, 0.2)' }}>
                <FaGamepad className="text-3xl text-blue-gray" />
              </div>
              <h2 className="text-3xl font-bold text-light-title"> {/* Cambiado a text-light-title */}
                Group Laser Tag Experiences
              </h2>
            </div>
            <p className="text-light-title text-lg mb-6 leading-relaxed">
              Gain full access to the battlefield with your crew. Select your favorite mission type and enjoy a focused, personalized match without outside players.
            </p>
            <ul className="space-y-3">
              {[
                "Custom game durations (30-90 minutes)",
                "Choose from 5+ mission types",
                "Dedicated game coordinator",
                "Priority equipment selection",
                "Team strategy briefing"
              ].map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-gray mr-2">✓</span>
                  <span className="text-light-title">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Public Sessions */}
      <section className="py-20 px-6" style={{ backgroundColor: brand.darkGray }}>
        <div 
          ref={publicRef}
          className={`max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center transition-smooth ${publicInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="text-left order-1 md:order-2">
            <div className="flex items-center mb-6">
              <div className="mr-4 p-3 rounded-full" style={{ backgroundColor: 'rgba(86, 109, 135, 0.2)' }}>
                <FaUsers className="text-3xl text-blue-gray" />
              </div>
              <h2 className="text-3xl font-bold text-light-title"> {/* Cambiado a text-light-title */}
                Join a Public Match
              </h2>
            </div>
            <p className="text-light-title text-lg mb-6 leading-relaxed">
              No reservation? No problem. Walk in and join a live match open to all. You'll be slotted into the current game mode and meet new players on the battlefield.
            </p>
            <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(20, 19, 24, 0.5)', border: `1px solid ${brand.blueGray}4D` }}>
              <h4 className="text-xl font-bold text-light-title mb-3"> {/* Cambiado a text-light-title */}
                Public Session Benefits:
              </h4>
              <ul className="grid grid-cols-2 gap-3">
                {[
                  "No minimum players",
                  "Flexible start times",
                  "Mix with other teams",
                  "Try new strategies",
                  "Competitive scoring",
                  "Instant gameplay"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-gray mr-2">•</span>
                    <span className="text-light-title text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="order-2 md:order-1">
            <ServiceCard 
              title="Live Action Games"
              text="Public sessions are a great way to jump into the excitement, try new tactics, and meet fellow laser tag enthusiasts."
              image={changinbattlegrounds}
              icon={<FaGamepad className="text-3xl text-blue-gray" />}
              titleColor="text-light-title" // Prop añadida
            />
          </div>
        </div>
      </section>

      {/* Birthday Party Area */}
      <section
        ref={partyRef}
        className={`py-28 px-6 text-center transition-smooth ${partyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{
          background: `radial-gradient(900px 400px at 10% -10%, rgba(255, 212, 4, 0.06), transparent 70%), radial-gradient(900px 500px at 110% 10%, rgba(86, 109, 135, 0.06), transparent 70%), ${brand.nearBlack}`
        }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="inline-block p-5 rounded-full mb-8" style={{ backgroundColor: 'rgba(86, 109, 135, 0.2)' }}>
            <FaBirthdayCake className="text-4xl text-blue-gray" />
          </div>
          <h2 className="text-4xl font-bold mb-8 text-light-title glow-text"> {/* Cambiado a text-light-title */}
            Celebrate at Lazer Zone
          </h2>
          <div className="grid md:grid-cols-2 gap-8 text-left mb-12">
            <div className="p-8 rounded-xl hover-glow transition-smooth" style={{ backgroundColor: 'rgba(31, 41, 55, 0.5)', border: `1px solid ${brand.blueGray}33` }}>
              <h3 className="text-2xl font-bold text-light-title mb-4 flex items-center"> {/* Cambiado a text-light-title */}
                <FaCalendarAlt className="text-blue-gray mr-3" />
                Party Lounge Only
              </h3>
              <p className="text-light-title mb-4">
                Reserve our stylish lounge with comfortable seating, party decorations, and food service options.
              </p>
              <ul className="space-y-2 text-light-title text-sm">
                <li>• 2-hour reservation</li>
                <li>• Tables & seating for up to 20</li>
                <li>• Basic decorations included</li>
                <li>• Food/drink packages available</li>
              </ul>
            </div>
            <div className="p-8 rounded-xl hover-glow transition-smooth" style={{ backgroundColor: 'rgba(31, 41, 55, 0.5)', border: `1px solid ${brand.blueGray}33` }}>
              <h3 className="text-2xl font-bold text-light-title mb-4 flex items-center"> {/* Cambiado a text-light-title */}
                <FaGamepad className="text-blue-gray mr-3" />
                Game + Party Package
              </h3>
              <p className="text-light-title mb-4">
                Combine laser tag gameplay with party space for the ultimate celebration experience.
              </p>
              <ul className="space-y-2 text-light-title text-sm">
                <li>• 1 hour of laser tag gameplay</li>
                <li>• 1.5 hours in party lounge</li>
                <li>• Custom team assignments</li>
                <li>• Scoreboard display</li>
                <li>• Champion certificates</li>
              </ul>
            </div>
          </div>
          <a
            href="/booking"
            aria-label="Reserve a Party Room"
            className="inline-flex items-center gap-3 mt-8 px-8 py-4 text-lg rounded-full shadow-lg transition-smooth focus:outline-none font-bold no-underline"
            style={{
              backgroundColor: brand.gold,
              color: brand.ink,
              border: `1px solid ${brand.gold}99`,
              boxShadow: '0 8px 20px rgba(255, 212, 4, 0.25)'
            }}
          >
            <FaCalendarAlt className="text-xl" />
            Reserve a Party Room
          </a>
        </div>
      </section>

      {/* Contact Info */}
      <section
        ref={contactRef}
        className={`py-20 px-6 text-center transition-smooth ${contactInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{ backgroundColor: brand.darkGray }}
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-light-title glow-text"> {/* Cambiado a text-light-title */}
            Visit Us in Cornwall
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <ContactItem 
              icon={<FaMapMarkerAlt className="text-3xl text-blue-gray" />}
              title="Location"
              info="917 Second St W, Cornwall, ON K6J 1H8"
              titleColor="text-light-title" // Prop añadida
            />
            <ContactItem 
              icon={<FaPhone className="text-3xl text-blue-gray" />}
              title="Call Us"
              info="(613) 703-0489"
              titleColor="text-light-title" // Prop añadida
            />
            <ContactItem 
              icon={<FaEnvelope className="text-3xl text-blue-gray" />}
              title="Email"
              info="info@lazerzonearena.ca"
              titleColor="text-light-title" // Prop añadida
            />
          </div>
          <div className="p-6 rounded-xl inline-block" style={{ backgroundColor: 'rgba(20, 19, 24, 0.5)' }}>
            <div className="flex items-center justify-center">
              <FaClock className="text-2xl text-blue-gray mr-3" />
              <h3 className="text-xl font-bold text-light-title"> {/* Cambiado a text-light-title */}
                Hours of Operation
              </h3>
            </div>
            <div className="mt-4 text-light-title">
              {settings?.opening_hours ? (
                Object.entries(settings.opening_hours).map(([day, hours]) => {
                  if (typeof hours === 'object' && hours !== null) {
                    if (hours.closed) {
                      return <p key={day}>{day}: Closed</p>;
                    } else {
                      return <p key={day}>{day}: {hours.open} – {hours.close}</p>;
                    }
                  } else {
                    // fallback for string or unexpected format
                    return <p key={day}>{day}: {hours}</p>;
                  }
                })
              ) : (
                <>
                  <p>Wednesday - Friday: 2 PM – 10 PM</p>
                  <p>Saturday - Sunday: 12 PM – 10 PM</p>
                </>
              )}
              <p className="mt-2 text-sm" style={{ color: brand.blueGray }}>*Extended hours available for private bookings</p>
            </div>
      {/* Contact Info Section */}
      <section className="py-20 px-6" style={{ backgroundColor: brand.darkGray }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          <ContactItem
            icon={<FaMapMarkerAlt className="text-2xl text-blue-gray mr-3" />}
            title="Address"
            info={settings?.address || 'Cornwall, ON'}
            titleColor="text-light-title"
            infoColor="text-light-title"
          />
          <ContactItem
            icon={<FaPhone className="text-2xl text-blue-gray mr-3" />}
            title="Phone"
            info={settings?.phone || '(613) 555-1234'}
            titleColor="text-light-title"
            infoColor="text-light-title"
          />
          <ContactItem
            icon={<FaEnvelope className="text-2xl text-blue-gray mr-3" />}
            title="Email"
            info={settings?.email || 'info@lazerzonearena.ca'}
            titleColor="text-light-title"
            infoColor="text-light-title"
          />
        </div>
      </section>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

const ServiceCard = ({ title, text, image, icon, titleColor = "text-light-title" }) => (
  <div className="relative group overflow-hidden rounded-xl shadow-xl hover-glow transition-smooth">
    <img
      src={image}
      alt={title}
      className="w-full h-96 object-cover transform group-hover:scale-105 transition-smooth"
    />
    <div
      className="absolute inset-0 flex items-end p-8"
      style={{
        background: `linear-gradient(to top, rgba(20, 19, 24, 0.8), rgba(20, 19, 24, 0.3), transparent)`
      }}
    >
      <div>
        <div className="flex items-center mb-4">
          <div className="mr-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(86, 109, 135, 0.3)' }}>
            {icon}
          </div>
          <h3 className={`text-2xl font-bold ${titleColor}`}>{title}</h3>
        </div>
        <p className="text-light-title">{text}</p>
      </div>
    </div>
  </div>
);

const ContactItem = ({ icon, title, info, titleColor = "text-light-title", infoColor = "text-light-title" }) => (
  <div
    className="p-6 rounded-xl hover-glow transition-smooth"
    style={{ backgroundColor: 'rgba(20, 19, 24, 0.3)', border: `1px solid ${brand.blueGray}33` }}
  >
    <div className="mb-4">{icon}</div>
    <h3 className={`text-xl font-bold mb-2 ${titleColor}`}>{title}</h3>
    <p className={`${infoColor}`}>{info}</p>
  </div>
);

export default Services;